import {
  createSlice,
  createAsyncThunk,
  createDraftSafeSelector,
} from '@reduxjs/toolkit';

import { api } from '../../api/api';
import { selectProjectUuid } from '../Project/projectSlice';

// Rejections are kept in state, so store a readable string rather than the
// axios error object (not serializable, not renderable).
function describeError(error) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail.message === 'string') return detail.message;
  return error?.message || 'Unknown error';
}

export const loadSequenceHistory = createAsyncThunk(
  'sequenceHistory/loadSequenceHistory',
  async ({ projectID, sequenceID }, thunkApi) => {
    const project = projectID || selectProjectUuid(thunkApi.getState());
    try {
      const entries = await api.sequences.history(project, sequenceID);
      return { sequenceID, entries: Array.isArray(entries) ? entries : [] };
    } catch (error) {
      return thunkApi.rejectWithValue({
        sequenceID,
        error: describeError(error),
      });
    }
  },
);

const initialState = {
  sequenceID: null,
  entries: [],
  loading: false,
  loaded: false,
  error: null,
};

const sequenceHistorySlice = createSlice({
  name: 'sequenceHistory',
  initialState,
  reducers: {
    resetSequenceHistory() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loadSequenceHistory.pending, (state, action) => {
        state.sequenceID = action.meta.arg.sequenceID;
        state.loading = true;
        state.loaded = false;
        state.error = null;
        state.entries = [];
      })
      .addCase(loadSequenceHistory.fulfilled, (state, action) => {
        // Ignore a stale response if the user switched sequence mid-flight.
        if (action.payload.sequenceID !== state.sequenceID) return;
        state.loading = false;
        state.loaded = true;
        state.entries = action.payload.entries;
      })
      .addCase(loadSequenceHistory.rejected, (state, action) => {
        const sequenceID = action.payload?.sequenceID ?? action.meta.arg.sequenceID;
        if (sequenceID !== state.sequenceID) return;
        state.loading = false;
        state.loaded = true;
        state.error =
          action.payload?.error || action.error?.message || 'Unknown error';
      });
  },
});

export const { resetSequenceHistory } = sequenceHistorySlice.actions;

const selectSelf = (state) => state.sequenceHistory;
export const selectSequenceHistory = createDraftSafeSelector(
  selectSelf,
  (s) => s.entries,
);
export const selectSequenceHistoryLoading = createDraftSafeSelector(
  selectSelf,
  (s) => s.loading,
);
export const selectSequenceHistoryLoaded = createDraftSafeSelector(
  selectSelf,
  (s) => s.loaded,
);
export const selectSequenceHistoryError = createDraftSafeSelector(
  selectSelf,
  (s) => s.error,
);

export default sequenceHistorySlice.reducer;
